import {DynamicDurationFormat} from "../../format/impl/DynamicDurationFormat";
import {SimpleDurationFormat} from "../../format/impl/SimpleDurationFormat";
import {ILocaProcessor} from "../../processors/api/ILocaProcessor";
import {HTMLTextComposer} from "../../processors/impl/flash/composer/HTMLTextComposer";
import {ILocalizationService} from "../api/ILocalizationService";
import {ILocalizeService} from "../api/ILocalizeService";
import {TimeType} from "../../utils/time/TimeType";
import {HTMLTextVO} from "../../vos/HTMLTextVO";
import {LocalizedTimeFormatVO} from "../../vos/LocalizedTimeFormatVO";
import {DateTimeStyle} from "../../globalization/DateTimeStyle";

/**
 * @private
 */
export class LocalizeService implements ILocalizeService {
    private _htmlTextComposer: HTMLTextComposer;
    private _simpleDurationFormat: SimpleDurationFormat;
    private _dynamicDurationFormat: DynamicDurationFormat;

    constructor(private _processor: ILocaProcessor, private _localizationService: ILocalizationService) {
        this._htmlTextComposer = new HTMLTextComposer();
        this._simpleDurationFormat = new SimpleDurationFormat(this._processor);
        this._dynamicDurationFormat = new DynamicDurationFormat(this._processor);
    }

    public text(textId: string, replacements: any = null, useNumberGrouping: boolean = true): string {
        if (this._localizationService.initialized) {
            const text = this._processor.text(textId, replacements, useNumberGrouping);

            // don't add another pipe if the text already ends with one; otherwise items would end up with 2, 3, or more pipes at the end
            if (this._localizationService.usePipe && this._localizationService.pipe && !text.endsWith(this._localizationService.pipe)) {
                return text + this._localizationService.pipe;
            } else {
                return text;
            }
        } else {
            // logger.error("The Localization Service is not initialized.");
        }

        return textId;
    }

    public hasText(textId: string): boolean {
        let result: boolean = false;

        if (this._localizationService.initialized) {
            result = this._processor.hasText(textId);
        } else {
            // logger.error("The Localization Service is not initialized.");
        }

        return result;
    }

    public number(
        numberValue: number,
        compact: boolean = false,
        fractionalDigits: number = -1,
        isRTL: boolean = false,
        useGrouping: boolean = true
    ): string {
        let result: string = numberValue.toString();

        if (this._localizationService.initialized) {
            result = this._processor.number(numberValue, compact, fractionalDigits, isRTL, useGrouping) + this._localizationService.pipe;
        } else {
            // logger.error("The Localization Service is not initialized.");
        }

        return result;
    }

    public integer(numberValue: number): string {
        if (this._localizationService.initialized) {
            return this._processor.integer(numberValue) + this._localizationService.pipe;
        } else {
            // logger.error("The Localization Service is not initialized.");
        }
    }

    public decimal(numberValue: number): string {
        if (this._localizationService.initialized) {
            return this._processor.decimal(numberValue) + this._localizationService.pipe;
        } else {
            // logger.error("The Localization Service is not initialized.");
        }
    }

    public datetime(date: Date, dateStyle: string = DateTimeStyle.SHORT, timeStyle: string = DateTimeStyle.SHORT): string {
        let result: string = date ? date.toString() : "";

        if (this._localizationService.initialized) {
            result = this._processor.datetime(date, dateStyle, timeStyle) + this._localizationService.pipe;
        } else {
            // logger.error("The Localization Service is not initialized.");
        }

        return result;
    }

     public digitalClock(time:string):string {
        return this._processor.digitalClock(time);
     }

    public numberForArabic(value: number): string {
        return this._processor.isArabic ? this._processor.number(value) : value.toString();
    }

    public time(
        seconds: number,
        trim: number = 2,
        leadingZero: boolean = true,
        localizedTimeFormatVO: LocalizedTimeFormatVO = null
    ): string {
        let result: string = seconds.toString();

        if (this._localizationService.initialized) {
            result = this._dynamicDurationFormat.apply(seconds, trim, leadingZero, localizedTimeFormatVO);
        } else {
            // logger.error("The Localization Service is not initialized.");
        }

        return result;
    }

    public timeCustom(format: string, seconds: number, roundTo: TimeType = null): string {
        let result: string = seconds.toString();

        if (this._localizationService.initialized) {
            result = this._simpleDurationFormat.apply(format, seconds);
        } else {
            // logger.error("...The Localization Service is not initialized.");
        }

        return result;
    }

    public htmlText(htmlTextVO: HTMLTextVO): string {
        let result: string = "";

        if (this._localizationService.initialized) {
            result = this._htmlTextComposer.by(htmlTextVO);
        } else {
            // logger.error("The Localization Service is not initialized.");
        }

        return result;
    }

    public get processorType(): string {
        return this._processor.processorType;
    }

    public set localizeReplacements(value: boolean) {
        this._processor.localizeReplacements = value;
    }

    public get localizeReplacements(): boolean {
        return this._processor.localizeReplacements;
    }

    public updateCurrencyFormatter(code: string, symbol: string): void {
        this._processor.updateCurrencyFormatter(code, symbol);
    }

    public formatCurrency(value: number, withSymbol: boolean = false): string {
        return this._processor.formateCurrency(value, withSymbol);
    }
}
